import { LocalStorageService } from './localStorageService';

interface ExtendedKeycloakTokenParsed {
  preferred_username?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  iat?: number;
  sub?: string;
}

interface UserInfo {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  sub?: string;
}

/**
 * Service for handling token operations with improved security
 */
export class TokenService {
  /**
   * Safely decode JWT token payload without signature validation
   * Note: This should only be used for reading claims, not for security validation
   */
  private static decodeTokenPayload(token: string): ExtendedKeycloakTokenParsed | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT token format');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      
      const decodedPayload = atob(paddedPayload);
      return JSON.parse(decodedPayload) as ExtendedKeycloakTokenParsed;
    } catch (error) {
      console.warn('Error decoding JWT token payload:', error);
      return null;
    }
  }

  /**
   * Check if token is valid with safety margin
   * @param token - JWT token to validate
   * @param safetyMarginSeconds - Safety margin in seconds (default 30)
   * @returns boolean indicating if token is valid
   */
  static isTokenValid(token: string | null | undefined, safetyMarginSeconds: number = 30): boolean {
    if (!token) {
      return false;
    }

    const tokenData = this.decodeTokenPayload(token);
    if (!tokenData || !tokenData.exp) {
      return false;
    }

    const expiryTime = tokenData.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const safetyMargin = safetyMarginSeconds * 1000;

    return (expiryTime - currentTime) > safetyMargin;
  }

  /**
   * Get token expiration time
   * @param token - JWT token
   * @returns Expiration time in milliseconds or null if invalid
   */
  static getTokenExpiration(token: string | null | undefined): number | null {
    if (!token) {
      return null;
    }

    const tokenData = this.decodeTokenPayload(token);
    if (!tokenData || !tokenData.exp) {
      return null;
    }

    return tokenData.exp * 1000; // Convert to milliseconds
  }

  /**
   * Get time until token expires
   * @param token - JWT token
   * @returns Time until expiration in milliseconds, or null if invalid
   */
  static getTimeUntilExpiration(token: string | null | undefined): number | null {
    const expirationTime = this.getTokenExpiration(token);
    if (!expirationTime) {
      return null;
    }

    return expirationTime - Date.now();
  }

  /**
   * Extract username from token
   * @param token - JWT token
   * @returns Username or empty string
   */
  static getUsername(token: string | null | undefined): string {
    if (!token) {
      return '';
    }

    const tokenData = this.decodeTokenPayload(token);
    return tokenData?.preferred_username || '';
  }

  /**
   * Extract user information from token
   * @param token - JWT token
   * @param realmRoles - Realm roles from Keycloak instance
   * @returns User information object
   */
  static getUserInfo(token: string | null | undefined, realmRoles: string[] = []): UserInfo {
    if (!token) {
      return {
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        roles: [],
        sub: undefined
      };
    }

    const tokenData = this.decodeTokenPayload(token);
    
    return {
      username: tokenData?.preferred_username || '',
      email: tokenData?.email || '',
      firstName: tokenData?.given_name || '',
      lastName: tokenData?.family_name || '',
      roles: realmRoles,
      sub: tokenData?.sub
    };
  }

  /**
   * Save tokens to storage
   * @param accessToken - Access token
   * @param refreshToken - Refresh token (optional)
   */
  static saveTokens(accessToken: string, refreshToken?: string): void {
    LocalStorageService.saveToken(accessToken);
    if (refreshToken) {
      LocalStorageService.saveRefreshToken(refreshToken);
    }
  }

  /**
   * Get stored tokens
   * @returns Object with access and refresh tokens
   */
  static getStoredTokens(): { accessToken: string | null; refreshToken: string | null } {
    return {
      accessToken: LocalStorageService.getToken(),
      refreshToken: LocalStorageService.getRefreshToken()
    };
  }

  /**
   * Clear all stored tokens
   */
  static clearTokens(): void {
    LocalStorageService.clearTokens();
  }

  /**
   * Log token information for debugging
   * @param token - JWT token
   * @param label - Label for logging
   */
  static logTokenInfo(token: string | null | undefined, label: string = 'Token'): void {
    if (!token) {
      console.log(`${label}: No token available`);
      return;
    }

    const timeUntilExpiration = this.getTimeUntilExpiration(token);
    if (timeUntilExpiration !== null) {
      const secondsUntilExpiration = Math.round(timeUntilExpiration / 1000);
      if (secondsUntilExpiration > 0) {
        console.log(`${label}: Valid, expires in ${secondsUntilExpiration} seconds`);
      } else {
        console.log(`${label}: Expired ${Math.abs(secondsUntilExpiration)} seconds ago`);
      }
    } else {
      console.log(`${label}: Invalid or malformed`);
    }
  }

  /**
   * Check if token needs refresh based on safety margin
   * @param token - JWT token
   * @param safetyMarginSeconds - Safety margin in seconds
   * @returns boolean indicating if token needs refresh
   */
  static needsRefresh(token: string | null | undefined, safetyMarginSeconds: number = 60): boolean {
    return !this.isTokenValid(token, safetyMarginSeconds);
  }
}